import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Search, User, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import ServiceDeskAPIService, {
  ptsOpenProjectListQueryKey,
  serviceDeskAgentAssignedProjectsQueryKey,
  serviceDeskUserProjectQueryKey,
} from "@sd/services/APIs";
import {
  ptsProjectTreeFromPayload,
  type PtsProjectNode,
} from "@sd/lib/ptsDropdownOptions";
import { getServiceDeskUserId, useAuth } from "@sd/hooks/useAuth";
import { isExternalSdRole } from "@sd/lib/sdRoles";
import { buildProjectTrackerReportPath } from "@sd/lib/trackerReportDrillDown";

function ProjectNameRow({
  name,
  nested,
  heading,
  onSelect,
}: {
  name: string;
  nested?: boolean;
  heading?: boolean;
  onSelect: () => void;
}) {
  return (
    <div className={cn("flex items-start gap-2", nested && "pl-4")}>
      <button
        type="button"
        onClick={onSelect}
        className={cn(
          "min-w-0 flex-1 text-left text-[13px] leading-snug text-[#1a6fb5] hover:underline",
          heading ? "font-bold" : "font-medium"
        )}
      >
        {name}
      </button>
      <User className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-400" aria-hidden />
    </div>
  );
}

function filterProjectTree(
  tree: PtsProjectNode[],
  query: string
): PtsProjectNode[] {
  const q = query.trim().toLowerCase();
  if (!q) return tree;
  const result: PtsProjectNode[] = [];
  for (const project of tree) {
    const parentMatches = project.name.toLowerCase().includes(q);
    if (parentMatches) {
      result.push(project);
      continue;
    }
    const matchingChildren = project.children.filter(child =>
      child.name.toLowerCase().includes(q)
    );
    if (matchingChildren.length > 0) {
      result.push({ ...project, children: matchingChildren });
    }
  }
  return result;
}

export function ProjectsIndex() {
  const navigate = useNavigate();
  const { role } = useAuth();
  const panel = role === "ServiceDesk_Admin" ? "admin" : "agent";
  const isExternalUser = isExternalSdRole(role);
  const isAgentOnlyRole = role === "ServiceDesk_Agent";
  const scopedUserId =
    isExternalUser || isAgentOnlyRole ? getServiceDeskUserId() : null;
  const externalUserId = isExternalUser ? scopedUserId : null;
  const agentEmpId = isAgentOnlyRole ? scopedUserId : null;

  const [search, setSearch] = useState("");

  const {
    data: assignedProjectsData,
    isPending: assignedProjectsPending,
    isFetching: assignedProjectsFetching,
    isError: assignedProjectsError,
  } = useQuery({
    queryKey: serviceDeskUserProjectQueryKey(externalUserId),
    queryFn: async () => {
      const response = await ServiceDeskAPIService.userProjects.list(
        externalUserId ?? undefined
      );
      return response.data;
    },
    enabled: Boolean(externalUserId),
    staleTime: 15_000,
  });

  const {
    data: agentAssignedProjectsData,
    isPending: agentAssignedProjectsPending,
    isFetching: agentAssignedProjectsFetching,
    isError: agentAssignedProjectsError,
  } = useQuery({
    queryKey: serviceDeskAgentAssignedProjectsQueryKey(agentEmpId),
    queryFn: async () => {
      const response = await ServiceDeskAPIService.agentAssignedProjects.list(
        agentEmpId ?? undefined
      );
      return response.data;
    },
    enabled: Boolean(agentEmpId),
    staleTime: 15_000,
  });

  const {
    data: ptsProjectsData,
    isPending: ptsProjectsPending,
    isFetching: ptsProjectsFetching,
    isError: ptsProjectsError,
  } = useQuery({
    queryKey: ptsOpenProjectListQueryKey,
    queryFn: async () => {
      const response = await ServiceDeskAPIService.pts.openProjectList();
      return response.data;
    },
    enabled: !isExternalUser && !isAgentOnlyRole,
    staleTime: 5 * 60 * 1000,
  });

  const data = isExternalUser
    ? assignedProjectsData
    : isAgentOnlyRole
      ? agentAssignedProjectsData
      : ptsProjectsData;
  const isPending = isExternalUser
    ? Boolean(externalUserId) && assignedProjectsPending
    : isAgentOnlyRole
      ? Boolean(agentEmpId) && agentAssignedProjectsPending
      : ptsProjectsPending;
  const isFetching = isExternalUser
    ? assignedProjectsFetching
    : isAgentOnlyRole
      ? agentAssignedProjectsFetching
      : ptsProjectsFetching;
  const isError = isExternalUser
    ? assignedProjectsError
    : isAgentOnlyRole
      ? agentAssignedProjectsError
      : ptsProjectsError;

  const projects = useMemo(() => ptsProjectTreeFromPayload(data), [data]);
  const filteredProjects = useMemo(
    () => filterProjectTree(projects, search),
    [projects, search]
  );

  const openProjectReport = (projectId: string) => {
    navigate(buildProjectTrackerReportPath(projectId, panel));
  };

  const trimmedSearch = search.trim();
  const searchActive = search.length > 0;

  return (
    <div className="min-h-full bg-[#f7f8fa] px-5 py-5 sm:px-8">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-[28px] font-normal leading-tight text-slate-800">
          Projects
        </h1>
        <div className="relative w-full sm:max-w-md">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search projects…"
            className={cn("h-9 pl-9", searchActive ? "pr-9" : "pr-3")}
            value={search}
            onChange={e => setSearch(e.target.value)}
            aria-label="Search projects"
          />
          {searchActive ? (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              onClick={() => setSearch("")}
              aria-label="Clear search"
              title="Clear"
            >
              <X className="h-3.5 w-3.5" />
            </Button>
          ) : null}
        </div>
      </div>

      {isPending ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : isError ? (
        <p className="rounded border border-slate-200 bg-white px-4 py-8 text-center text-sm text-slate-500">
          Could not load projects. Please try again.
        </p>
      ) : filteredProjects.length === 0 ? (
        <p className="rounded border border-slate-200 bg-white px-4 py-8 text-center text-sm text-slate-500">
          {trimmedSearch
            ? `No projects match “${trimmedSearch}”.`
            : "No projects available."}
        </p>
      ) : (
        <div
          className={cn(
            "columns-1 gap-4 sm:columns-2 xl:columns-3 2xl:columns-4",
            isFetching && "opacity-70"
          )}
        >
          {filteredProjects.map(project => (
            <div
              key={project.id}
              className="mb-4 break-inside-avoid rounded-sm border border-slate-200 bg-white px-3 py-2.5"
            >
              <ProjectNameRow
                name={project.name}
                heading
                onSelect={() => openProjectReport(project.id)}
              />
              {project.children.length > 0 ? (
                <div className="mt-1.5 space-y-1.5">
                  {project.children.map(child => (
                    <ProjectNameRow
                      key={child.id}
                      name={child.name}
                      nested
                      onSelect={() => openProjectReport(child.id)}
                    />
                  ))}
                </div>
              ) : null}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
